// Hook to calculate menu item availability based on ingredient stock
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface MissingIngredient {
  ingredientId: string;
  nameAr: string;
  nameFr: string;
  emoji: string;
  reason: 'no_stock' | 'insufficient' | 'unit_mismatch';
}

export interface MenuItemAvailability {
  menuItemId: string;
  availableQuantity: number;
  isAvailable: boolean;
  missingIngredients?: MissingIngredient[];
}

/**
 * Fetch availability for all menu items in a restaurant
 *
 * NOTE: This hook does NOT set up its own realtime subscriptions.
 * Instead, it relies on explicit invalidation from mutations (useCreateOrder, useUpdateOrderStatus, etc.)
 * This prevents duplicate subscriptions and reduces API calls.
 */
export function useMenuItemAvailability(restaurantId: string | null) {
  return useQuery({
    queryKey: ['menuItemAvailability', restaurantId],
    staleTime: 2 * 60 * 1000, // 2 minutes - mutations invalidate when needed
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false, // Disabled - rely on explicit invalidation
    refetchOnMount: false, // Don't refetch if data exists
    queryFn: async (): Promise<Record<string, MenuItemAvailability>> => {
      if (!restaurantId) {
        return {};
      }

      // Fetch availability and missing ingredients in parallel
      const [availabilityResult, missingResult] = await Promise.all([
        (supabase.rpc as any)('calculate_menu_item_availability', {
          p_restaurant_id: restaurantId,
          p_menu_item_id: null,
          p_menu_item_option_id: null,
        }),
        (supabase.rpc as any)('get_menu_item_missing_ingredients', {
          p_restaurant_id: restaurantId,
          p_menu_item_id: null,
        }),
      ]);

      if (availabilityResult.error) {
        console.error('Error fetching menu item availability:', availabilityResult.error);
        return {};
      }

      // Build missing ingredients map
      const missingMap: Record<string, MissingIngredient[]> = {};
      if (!missingResult.error && Array.isArray(missingResult.data)) {
        missingResult.data.forEach((item: any) => {
          missingMap[item.menu_item_id] = item.missing_ingredients || [];
        });
      }

      // Convert array to map for easy lookup
      const availabilityMap: Record<string, MenuItemAvailability> = {};
      if (Array.isArray(availabilityResult.data)) {
        availabilityResult.data.forEach((item: any) => {
          const missingIngredients = missingMap[item.menu_item_id];
          const availability: MenuItemAvailability = {
            menuItemId: item.menu_item_id,
            availableQuantity: item.available_quantity,
            isAvailable: item.is_available,
          };
          if (missingIngredients) {
            availability.missingIngredients = missingIngredients;
          }
          availabilityMap[item.menu_item_id] = availability;
        });
      }

      return availabilityMap;
    },
    enabled: !!restaurantId,
  });
}

/**
 * Fetch availability for a specific menu item with a specific option
 *
 * NOTE: This hook does NOT set up its own realtime subscriptions.
 * Instead, it relies on explicit invalidation from mutations.
 */
export function useMenuItemOptionAvailability(
  restaurantId: string | null,
  menuItemId: string | null,
  menuItemOptionId: string | null
) {
  return useQuery({
    queryKey: ['menuItemOptionAvailability', restaurantId, menuItemId, menuItemOptionId],
    staleTime: 2 * 60 * 1000, // 2 minutes - mutations invalidate when needed
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false, // Disabled - rely on explicit invalidation
    refetchOnMount: false, // Don't refetch if data exists
    queryFn: async (): Promise<MenuItemAvailability | null> => {
      if (!restaurantId || !menuItemId) {
        return null;
      }

      const { data, error } = await (supabase.rpc as any)('calculate_menu_item_availability', {
        p_restaurant_id: restaurantId,
        p_menu_item_id: menuItemId,
        p_menu_item_option_id: menuItemOptionId,
      });

      if (error) {
        console.error('Error fetching menu item option availability:', error);
        return null;
      }

      if (!Array.isArray(data) || data.length === 0) {
        return null;
      }

      const item = data[0];
      return {
        menuItemId: item.menu_item_id,
        availableQuantity: item.available_quantity,
        isAvailable: item.is_available,
      };
    },
    enabled: !!restaurantId && !!menuItemId,
  });
}







